import React, { forwardRef, PropsWithChildren, RefObject } from 'react';
import BtnPrimary from 'src/components-v2/Inputs/Button/BtnPrimary';
import { Column, Container, Row } from 'src/components-v2/Layout';
import { Button as ButtonLink } from 'src/components-v3/Homepage/Buttons/Button';
import { useLazyGuilloche } from 'src/hooks/useLazyGuilloche';
import useWorkerGuilloche from 'src/hooks/useWorkerGuilloche';
import Box from 'src/components-v2/Layout/Box/Box';
import Stack from 'threads5/Stack';
import styled from 'threads5/styles/styled';
import GlowingCard from './GlowingCard';
import CodeBlock from './CodeBlock';

const Heading2 = styled('h2')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '7rem',
    lineHeight: '113%',
    letterSpacing: '-2.8px',
    color: theme.palette['base/white'],
    [theme.breakpoints.down('xl')]: {
      fontSize: '6.7rem',
      letterSpacing: '-1.6px',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '4.8rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '3.6rem',
    },
    [theme.breakpoints.only('xs')]: {
      fontSize: '3rem',
    },
  };
});

const Description = styled('p')(({ theme }) => {
  return {
    marginBottom: 0,
    textAlign: 'center',
    fontSize: '2.4rem',
    lineHeight: '150%' /* 72.8px */,
    color: '#fff',
    [theme.breakpoints.down('lg')]: {
      fontSize: '2.2rem',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
    },
    [theme.breakpoints.only('xs')]: {
      fontSize: '1.6rem',
    },
  };
});

const CodeBlockGrid: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Row
      sx={{
        width: '100%',
      }}
      columns={22}
    >
      <Column xsOffset={2} xs={18} mdOffset={3} md={16} lgOffset={3} lg={16}>
        {children}
      </Column>
    </Row>
  );
};

const APISectionGuillocheCanvas = forwardRef(
  ({}, ref: RefObject<HTMLCanvasElement>) => {
    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: {
            xs: '-150px',
            sm: '-150px',
            md: '-200px',
            lg: '-220px',
          },
          width: {
            xs: '250%',
            sm: '200%',
            md: '150%',
            lg: '100%',
          },
          zIndex: -2,
        }}
      >
        <canvas
          ref={ref}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
          }}
        />
      </Box>
    );
  },
);

const APISectionGuilloche = () => {
  const canvas5 = React.useRef<HTMLCanvasElement>(null);
  const { guilloche: dune, isGuillocheLoaded: isDuneLoaded } = useLazyGuilloche(
    {
      pattern: 'dune',
      canvasRef: canvas5,
    },
  );
  useWorkerGuilloche({
    canvasRef: canvas5,
    svgPaths: dune?.paths,
    svgWidth: dune?.width,
    svgHeight: dune?.height,
    intensity: 0.2,
    speed: 0.1,
    horizontalWave: true,
    verticalWave: true,
    zAxisWave: true,
    id: 'developer-canvas',
    scale: { xl: 1.2, xxl: 1.5 },
    y: { sm: -15, md: -15, xl: -11, xxl: -5 },
    disableAnimation: !isDuneLoaded,
  });

  return <APISectionGuillocheCanvas ref={canvas5} />;
};

const APISection: React.FC<{
  isGuillocheDisabled: boolean;
}> = ({ isGuillocheDisabled }) => {
  return (
    <Container>
      <Row
        sx={{
          height: {
            xs: '71rem',
            sm: '76rem',
            md: '78rem',
            lg: '85rem',
          },
          paddingTop: {
            xs: '6rem',
            lg: 0,
          },
          marginTop: {
            lg: '-250px',
            xl: '-400px',
          },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Column xsOffset={1} xs={22}>
          <GlowingCard
            isLazy={true}
            sx={{
              height: {
                xs: '64rem',
                sm: '65rem',
                md: '73rem',
                lg: '80rem',
              },
              maxHeight: '88rem',
              background: '#0D0D3F',
              alignItems: 'flex-end',
            }}
            containerSx={{
              height: {
                xs: '64rem',
                sm: '65rem',
                md: '73rem',
                lg: '80rem',
              },
              maxHeight: '88rem',
              position: 'relative',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
                height: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%',
                gap: {
                  xs: '3.6rem',
                  sm: '4rem',
                  // xl: '4rem',
                },
                paddingTop: {
                  xs: '4rem',
                  sm: '5rem',
                  md: '6rem',
                  lg: '7rem',
                },
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '100%',
                  gap: {
                    xs: '2rem',
                    sm: '2.7rem',
                  },
                }}
              >
                <CodeBlockGrid>
                  <Heading2>
                    Want access to 12K banks?
                    <br />
                    We've got the API keys.
                  </Heading2>
                </CodeBlockGrid>

                <CodeBlockGrid>
                  <Description>
                    You build the experience. We'll handle the data.
                  </Description>
                </CodeBlockGrid>
                <Stack direction={{ sm: 'row' }} flexWrap='wrap' gap='29px'>
                  <ButtonLink
                    variant='elizabeth'
                    href='https://dashboard.plaid.com/signup'
                  >
                    Get the API keys
                  </ButtonLink>
                  <BtnPrimary
                    href='/docs'
                    sx={{
                      justifyContent: 'center',
                    }}
                  >
                    Read the docs
                  </BtnPrimary>
                </Stack>
              </Stack>

              <CodeBlockGrid>
                <Box
                  sx={{
                    position: 'relative',
                    '&:hover .codeGlow': {
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  <CodeBlock />
                </Box>
              </CodeBlockGrid>
            </Box>

            {!isGuillocheDisabled && <APISectionGuilloche />}
          </GlowingCard>
        </Column>
      </Row>
    </Container>
  );
};

export default APISection;
